import { BookingAnalyticsApi } from './bookingAnalyticsApi';

/**
 * Unwrap the analytics API response.
 *
 * The backend can return two shapes:
 *  - NEW (flat):   { meta: {...}, booking: {...}, cancelling: {...}, rescheduling: {...} }
 *  - OLD (wrapped):{ success: true, data: { meta: {...}, booking: {...}, ... } }
 *
 * We detect by checking for the `booking` key at the root.
 */
const unwrapAnalyticsResponse = (response) => {
  if (!response || typeof response !== 'object') {
    throw new Error('Invalid analytics response from server');
  }

  // Determine the payload — flat (keys at root) or wrapped (under .data)
  let payload = null;
  if (response.booking !== undefined) {
    payload = response;           // flat: booking/cancelling/rescheduling at root
  } else if (response.data?.booking !== undefined) {
    payload = response.data;      // wrapped: payload under .data
  } else {
    throw new Error('Unexpected analytics response shape — neither flat nor wrapped');
  }

  // Return only the expected keys — strips success/message/from_cache etc.
  // that the backend now sends at the root level alongside the real data.
  return {
    booking:            payload.booking,
    cancelling:         payload.cancelling,
    rescheduling:       payload.rescheduling,
    meta:               payload.meta ?? {},
    // appointment_update: new key added by backend — included for future UI use
    appointment_update: payload.appointment_update ?? null,
  };
};

/**
 * Fetch aggregated analytics metrics from the real backend.
 * Endpoint: GET /api/admin/v1/booking-analytics/analytics
 * @param {{ date_from?: string, date_to?: string }} params
 */
export const fetchAnalytics = async (params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  );
  const response = await BookingAnalyticsApi.getDashboard(clean);
  return unwrapAnalyticsResponse(response);
};

/**
 * Fetch paginated trace logs.
 * Endpoint (prod pattern): GET /api/admin/v1/trace-logs/page/{page}
 * Response shape: {
 *   success: true,
 *   data: {
 *     data: [...sessions],
 *     meta: { total, per_page, current_page, last_page, last_updated },
 *     summary: { total, confirmed, expired, abandoned, failed, pending }
 *   }
 * }
 * @param {{ page?: number, per_page?: number, status?: string, module?: string, search?: string, start_date?: string, end_date?: string }} params
 */
export const fetchTraceLogs = async (params = {}) => {
  const { page = 1, ...filters } = params;
  // Strip empty / 'all' values from filter params
  const clean = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v != null && v !== '' && v !== 'all')
  );
  const response = await BookingAnalyticsApi.getLogs({ page, ...clean });
  if (!response || typeof response !== 'object') {
    throw new Error('Invalid trace logs response from server');
  }
  return response;
};


/**
 * Fetch single trace log detail.
 * Endpoint: GET /api/admin/v1/booking-analytics/trace-logs/{session_id}
 * @param {string} sessionId
 */
export const fetchTraceLogDetail = async (sessionId) => {
  const response = await BookingAnalyticsApi.getLogDetail(sessionId);
  if (!response || typeof response !== 'object') {
    throw new Error('Invalid trace log detail response from server');
  }
  return response?.data ?? response;
};

/**
 * Trigger a data export and initiate a browser download.
 * Endpoint: POST /api/admin/v1/booking-analytics/export
 * @param {string} type   e.g. 'sessions_csv' | 'weekly_csv' | 'metrics_json'
 * @param {{ date_from?: string, date_to?: string }} params
 */
export const exportAnalyticsData = async (type, params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  );
  // BookingAnalyticsApi.export returns full Axios response (responseType: 'blob')
  const response = await BookingAnalyticsApi.export({ type, ...clean });
  const rawData = response?.data ?? response;
  const contentType = response?.headers?.['content-type'] || 'text/csv';
  const blobData = rawData instanceof Blob ? rawData : new Blob([rawData], { type: contentType });
  const url = URL.createObjectURL(blobData);
  const a = document.createElement('a');
  a.href = url;
  // Try to get filename from Content-Disposition header
  const cd = response?.headers?.['content-disposition'] || '';
  const match = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  a.download = match ? match[1].replace(/['"]/g, '') : `analytics_${type}_${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Trigger manual data sync.
 * Endpoint: POST /api/admin/v1/booking-analytics/sync
 * @param {{ force?: boolean, from?: string, to?: string }} [data]
 */
export const triggerSync = async (data = {}) => {
  const response = await BookingAnalyticsApi.sync(data);
  return response?.data ?? response;
};

/**
 * Check manual data sync status.
 * Endpoint: GET /api/admin/v1/booking-analytics/sync/status
 */
export const fetchSyncStatus = async () => {
  const response = await BookingAnalyticsApi.getSyncStatus();
  return response?.data ?? response;
};
